import {
  Action,
  AllActions,
  DeployedActions,
  DeploymentUnitStatus,
  NotDeployedActions,
  ProvisioningPreviewResponseOperation,
  TargetStatus,
} from '@agilelab/plugin-wb-builder-common';
import { DeploymentPreview } from '../types';
import { snakeCaseToTitleCase } from '@agilelab/plugin-wb-platform-common';

export type Preview = {
  action: Action;
  targetStatus: TargetStatus;
  componentId: string;
};

/**
 * Converts an array of DeploymentPreview objects to an array of Preview objects
 * @param  {DeploymentPreview[]} previews The input array
 * @param cleanUpPreviews
 * @param  {Boolean} extractSubcomponents If true, subcomponents from each DeploymentPreview will be extracted and added to the result array as well (in a flattened way)
 * @return {Preview[]} The result array
 */
export const createPreviews = (
  previews: DeploymentPreview[],
  cleanUpPreviews: DeploymentPreview[] = [],
  extractSubcomponents: boolean = true,
): Preview[] => {
  const addPreviews = (items: DeploymentPreview[], result: Preview[]) => {
    items.forEach(p => {
      result.push({
        action: p.action!,
        targetStatus: p.newState!,
        componentId: p.id,
      });
      if (extractSubcomponents && p.components) {
        p.components.forEach(c => {
          result.push({
            action: c.action!,
            targetStatus: c.newState!,
            componentId: c.id,
          });
        });
      }
    });
    return result;
  };

  let newPreviews: Preview[] = [];
  newPreviews = addPreviews(previews, newPreviews);
  newPreviews = addPreviews(cleanUpPreviews, newPreviews);

  return newPreviews;
};

export const buildConflictPreviewMessage = (
  previews: DeploymentPreview[],
  conflicts: ProvisioningPreviewResponseOperation[],
): string => {
  const previewMap: { [key: string]: DeploymentPreview } = Object.fromEntries(
    previews.map(preview => [preview.id, preview]),
  );

  const conflictsMessage = conflicts.map(conflict => {
    const reasons: string[] = [];
    if (conflict.reason === 'DEPENDANT_ON' && conflict.dependantOn) {
      reasons.push(
        `Component is dependant on ${conflict.dependantOn
          .map(id => previewMap[id].name ?? id)
          .join(', ')}`,
      );
    }

    return `${
      previewMap[conflict.componentId].name ?? conflict.componentId
    } cannot transition into state ${snakeCaseToTitleCase(
      previewMap[conflict.componentId].newState ?? TargetStatus.DEPLOYED,
    )}. Reasons: ${reasons.join(', ')} `;
  });

  const str = `Aborted for conflicts: ${conflictsMessage.join('; ')}`;

  return str;
};

export const getAllowedActions = (
  currentState: DeploymentUnitStatus,
  newState: TargetStatus,
) => {
  const isDeployTarget = newState === TargetStatus.DEPLOYED;

  switch (currentState) {
    case DeploymentUnitStatus.CORRUPT:
      return isDeployTarget ? DeployedActions : NotDeployedActions;

    case DeploymentUnitStatus.NOT_DEPLOYED:
      return isDeployTarget
        ? DeployedActions
        : [Action.NO_ACTION, Action.UNDEPLOY, Action.DISCARD];

    case DeploymentUnitStatus.DEPLOYED:
      return isDeployTarget
        ? [Action.DEPLOY, Action.NO_ACTION]
        : NotDeployedActions;

    case DeploymentUnitStatus.PARTIALLY_DEPLOYED:
    case DeploymentUnitStatus.PROVISIONING_IN_PROGRESS:
      return AllActions;

    default:
      throw new Error('Unhandled DeploymentUnitStatus');
  }
};
